import * as Sentry from '@sentry/react';
import {clientLog} from "../clientLog";

/**
 * For each place in model.content.places, check if the reference.title
 * matches the reference.content.name. If not, update the reference.title
 * to match the reference.content.name.
 * Remove possible duplicates.
 * @param {object} models - The models to update
 * @param {boolean} keepNameAndTitleRef - Whether to keep the reference.nameAndTitle property or not. Default is false.
 * @private
 */
export const patchPlaceNames = (models, keepNameAndTitleRef = false) => {

    const cleanStr = arg => {
        if (arg?.indexOf('(') > -1) {
            arg = arg?.substring(0, arg?.indexOf('(')).trim();
        }
        return arg;
    };

    for (let j = 0, max = models.length; j < max; j++) {
        const {content} = models[j];
        const {places} = content;

        if (!places || places?.length === 0) {
            continue;
        }

        for (let i = 0, max = places.length; i < max; i++) {
            const place = places[i];

            if (!place?.reference) {
                continue;
            }

            let title = place.reference.title;

            // For some models, the title property is an empty string, but the reference.content object is present,
            // restore the title property!
            if (title === '' &&
                typeof (place.reference.content) !== 'undefined' &&
                typeof (place.reference.content.name) !== 'undefined'
            ) {
                console.warn('patching place title: ', JSON.stringify(place));
                title = cleanStr(place.reference.content.name);
                place.reference.title = title;
            }

            // Existing objects does not have the reference.content object,
            // but the nameAndTitle property is stored under reference.nameAndTitle.
            // reference.nameAndTitle must be moved to reference.content.nameAndTitle.
            // In addition, the reference.title must be the "clean" version without any extra data.
            const nameAndTitle = place.reference?.nameAndTitle;
            if (typeof (place.reference.content) === 'undefined') {
                console.warn('patching place content: ', JSON.stringify(place));
                place.reference.content = {
                    name: cleanStr(title),
                    nameAndTitle: nameAndTitle
                }
            }

            place.reference.title = cleanStr(title);
            if (!keepNameAndTitleRef) {
                delete place.reference.nameAndTitle;
            }

            verifyPlace(place);

            models[j].content.places[i] = {...place};
        }

        // Remove possible duplicates, that has not been issued a collection ID.
        models[j].content.places = models[j].content.places.filter(p => p?.collectionId);
    }
};

/**
 * Verify that the place object has the required properties. If not, log a warning to Sentry.
 * @param place
 * @returns {boolean}
 */
const verifyPlace = place => {
    let msg = '';
    if (!place.reference) {
        msg = 'place.reference is missing';
    }
    if (!place.reference.title) {
        msg = 'place.reference.title is missing';
    }
    if (!place.reference.content) {
        msg = 'place.reference.content is missing';
    }
    if (!place.reference.content.name) {
        msg = 'place.reference.content.name is missing';
    }
    if (msg) {
        clientLog('warning', `${msg} - ${JSON.stringify(place)}`);
        Sentry.captureMessage(`${msg} - ${JSON.stringify(place)}`, "warning");
    }
    return true;
};
